/**
 * This file is the entry point for the React app, it sets up the root
 * element and renders the Root component to the DOM.
 *
 * It is included in `src/index.html`.
 */
import "./index.css";

import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { MapProvider } from "react-map-gl/mapbox";
import { BrowserRouter, Route, Routes } from "react-router";

import { ZeroInit } from "@/zero-init";

import { LoginProvider } from "./hooks/use-login";
import { RoomProvider } from "./hooks/use-room";
import { ZeroStatusProvider } from "./hooks/use-zero-status";
import MapLayout from "./layout";
import Home from "./routes/home";
import Login from "./routes/login";
import Register from "./routes/register";
import RoomDetail from "./routes/rooms/room-detail";
import RoomList from "./routes/rooms/room-list";
import DocumentList from "./routes/documents/document-list";
import DocumentDetail from "./routes/documents/document-detail";

const queryClient = new QueryClient();

const app = (
  <QueryClientProvider client={queryClient}>
    <LoginProvider>
      <ZeroStatusProvider>
        <ZeroInit>
          <StrictMode>
            <BrowserRouter>
              <RoomProvider>
                <MapProvider>
                  <MapLayout>
                    <Routes>
                      <Route path="/" element={<Home />} />
                      <Route path="/login" element={<Login />} />
                      <Route path="/register" element={<Register />} />
                      <Route path="/rooms/" element={<RoomList />} />
                      <Route path="/rooms/:roomId" element={<RoomDetail />} />
                      <Route path="/documents" element={<DocumentList />} />
                      <Route
                        path="/documents/:id"
                        element={<DocumentDetail />}
                      />
                    </Routes>
                  </MapLayout>
                </MapProvider>
              </RoomProvider>
            </BrowserRouter>
          </StrictMode>
        </ZeroInit>
      </ZeroStatusProvider>
    </LoginProvider>
  </QueryClientProvider>
);

const elem = document.getElementById("root")!;

// @ts-ignore
if (import.meta.hot) {
  // With hot module reloading, `import.meta.hot.data` is persisted.
  // @ts-ignore
  const root = (import.meta.hot.data.root ??= createRoot(elem));
  root.render(app);
} else {
  // The hot module reloading API is not available in production.
  createRoot(elem).render(app);
}
